import { useMutation, useQuery, useQueryClient } from 'react-query';

import { queryCreate, queryGet } from './fetch';
import { useCallback } from 'react';
import { CriterionStatus, CriterionStatusEnum } from '.';
import { Session } from './session';
import { Recruitment } from './recruitment';
import { Form } from './form';
import { useGetAnswerText } from './utils/useGetAnswerText';

export interface FormResponse {
  id?: string;
  formId?: string;
  startedAt?: Date;
  finishedAt?: string;
  answers: FieldAnswer[];
  participantId?: string;
}

export interface CreateFormResponse {
  formId?: string;
  startedAt?: Date;
  answers: FieldAnswer[];
}

export interface FieldAnswerChoice {
  label: string;
  ref: string;
  value?: any;
  metadata?: any;
}

export interface FieldAnswerChoices {
  labels: string[];
  refs: string[];
  values?: any[];
  metadata?: any[];
}

export interface FieldAnswer {
  type: keyof FieldAnswer;
  id?: string;

  field: {
    id: string;
    ref: string;
    title: string;
    type: string;
  };

  text?: string;
  choices?: FieldAnswerChoices;
  choice?: FieldAnswerChoice;
  date?: string;
  number?: number;
}

export interface StartAnswer {
  id?: string;
  formId: string;
  participantId?: string;
}

export interface AnswersByFieldChoices {
  choices: Array<{
    label: string;
    answerCount: number;
  }>;
  none: number;
  others: string[];
}

export enum AnswersByFieldType {
  TEXTS = 'texts',
  CHOICES = 'choices',
}

export interface AnswersByField {
  id: string;
  formId: string;
  fieldId: string;
  answersCount: number;
  type: AnswersByFieldType;
  texts: string[];
  choices: AnswersByFieldChoices;
}

export const useStartAnswer = () => {
  return useMutation<{ id: string }, unknown, StartAnswer>(
    queryCreate('/v2/form-responses'),
  );
};

export const useSendFieldAnswer = (id?: string) => {
  return useMutation<
    void,
    unknown,
    FieldAnswer & { participantId?: string; formId?: string }
  >(queryCreate(`/v2/form-responses/${id}/fields-answers`));
};

export const useFinishAnswer = (id?: string) => {
  return useMutation<void, unknown, void>(
    queryCreate(`/v2/form-responses/${id}/finish`),
  );
};

export const usePrefetchFormResponse = () => {
  const queryClient = useQueryClient();

  return useCallback(
    async (id: string) => {
      if (queryClient.getQueryData(['form-responses', id])) {
        return;
      }
      queryClient.prefetchQuery(
        ['form-responses', id],
        queryGet(`/v2/form-responses/${id}`),
      );
    },
    [queryClient],
  );
};

export const getListResultsByParticipant = (id: string) => {
  return queryGet(`/v2/forms/${id}/form-responses`)();
};

export const useListResultsByParticipant = (id?: string) => {
  return useQuery<FormResponse[]>(
    ['forms', id, 'form-responses'],
    queryGet(`/v2/forms/${id}/form-responses`),
    {
      enabled: !!id,
    },
  );
};

export interface AnswerText {
  fieldRef: string;
  question: string;
  answer: string;
  status?: CriterionStatusEnum;
}

export const useApplicationAnswersQuery = () => {
  const getAnswerText = useGetAnswerText();
  return useCallback(
    (applicationId?: string) => async (): Promise<AnswerText[]> => {
      if (!applicationId) {
        return [];
      }
      const [response, status]: [FormResponse, CriterionStatus[]] =
        await Promise.all([
          queryGet(`/v2/applications/${applicationId}/form-response`)(),
          queryGet(`/v2/applications/${applicationId}/form-answers-status`)(),
        ]);

      return response.answers.map((answer) => ({
        fieldRef: answer.field.ref,
        question: answer.field.title,
        answer: getAnswerText(answer),
        status:
          status?.find((s) => s.fieldRef === answer.field.ref)?.status ||
          CriterionStatusEnum.IGNORE,
      }));
    },
    [getAnswerText],
  );
};

export const useApplicationAnswers = (
  applicationId?: string,
  sessionId?: string,
) => {
  const queryClient = useQueryClient();
  const query = useApplicationAnswersQuery();

  const res = useQuery<AnswerText[]>(
    ['applications', applicationId, 'answers-texts'],
    query(applicationId),
    {
      enabled: !!applicationId,
      retry: 1,
    },
  );

  return {
    ...res,
    data: res.data?.map((answer): AnswerText => {
      if (answer.question) {
        return answer;
      }
      const session = queryClient.getQueryData<Session>([
        'sessions',
        sessionId,
      ]);
      const recruitment = queryClient.getQueryData<Recruitment>([
        'recruitments',
        session?.recruitmentId,
      ]);
      const form = queryClient.getQueryData<Form>([
        'forms',
        recruitment?.screeningFormId,
      ]);
      if (!form?.fields) {
        return answer;
      }
      return {
        ...answer,
        question:
          form.fields?.find((f) => f.ref === answer.fieldRef)?.title || '',
      };
    }),
  };
};
